/* eslint-disable react/no-unknown-property */
/** @jsxImportSource @emotion/react */
import { Global, useTheme } from '@emotion/react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Text from '@Text';
import makeFailureNoticeStyles from './makeFailureNoticeStyles';

/**
 * Vertically centres the failure interstitial within the full-height form area.
 *
 * `#inputAreaParentContainer` is the first ancestor that spans the whole page;
 * its child (`inputAreaContainer`) is short. The `:has()` rule makes that
 * ancestor a centred flex column ONLY while a failure notice is mounted (marked
 * with `data-olb-failure`), so it applies synchronously with the failure render
 * — no measuring, no post-mount reflow, no jump — and never affects the confirm
 * page or any other page, whose content carries no such marker.
 */
const centreOnFailure = {
  // `#inputAreaParentContainer` carries an inline `display: block` (set by the
  // core InputArea), which outranks a stylesheet selector — so `display: flex`
  // must be `!important` to win, otherwise the flex props sit inert under a
  // block box. Scoped to `:has([data-olb-failure])`, so it only ever applies
  // while a failure notice is mounted.
  '#inputAreaParentContainer:has([data-olb-failure])': {
    display: 'flex !important',
    flexDirection: 'column',
    justifyContent: 'center',
    // Light mint background from the design (sampled from Billy's screenshot).
    backgroundColor: '#e8f3f1',
  },
};

/**
 * Booking-flow failure interstitial: a phone icon above a bold heading and a
 * muted subheading, shown briefly before the journey falls through to a normal
 * lead. Used for both the store-lookup failure and the booking failure — the
 * caller supplies the (localized) heading/subheading and the test id.
 */
const FailureNotice = ({ heading, subheading, dataTestId }) => {
  const theme = useTheme();
  const classes = makeFailureNoticeStyles({ theme });

  return (
    <>
      <Global styles={centreOnFailure} />
      <Container
        css={classes.container}
        data-testid={dataTestId}
        data-olb-failure
      >
        <Box css={classes.icon} data-testid="failurePhoneIcon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="40"
            height="40"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
          </svg>
        </Box>
        <Box css={classes.heading}>
          <Text variant="question">{heading}</Text>
        </Box>
        <Box css={classes.subheading}>
          <Text variant="text">{subheading}</Text>
        </Box>
      </Container>
    </>
  );
};

export default FailureNotice;
